import React, {useMemo, useState} from 'react'
import InfoTooltip from "../../components/InfoTooltip.js";
import TalentMatchDetails from "./TalentMatchDetails";
import CircleProgress from "../../components/CircleProgress";
import {ProgressBar} from "react-step-progress-bar";
import {Img} from "react-image";
import LoadingSpinner from "../../components/LoadingSpinner";
import {Link, useLocation, useSearchParams} from "react-router-dom";
import {useTalent} from "../../providers/TalentProvider";
import UserImageCircle from "../../components/UserImageCircle";
import {Button, Checkbox, Dropdown, Rate} from "antd";
import TalentReviewModal from './TalentReviewModal.js';
import {useAuth} from '../../providers/AuthProvider.js';
import SpinLoader from "../../components/SpinLoader";
import TalentTileRate from "./TalentTileRate";
import ClientPreferredRateModal from "./ClientPreferredRateModal";
import TalentTileBadges from "./TalentTileBadges";
import {CloseOutlined} from "@ant-design/icons";

const TalentStatusActions = ({ onTalentAction, isDisabled, canRunBulkActions, onAddToBulkActions, isBulkActionsSelected, bulkActions = [], ...props }) => {
    const { auth } = useAuth();

    const {
        talent,
        canFavorite,
        removeFromFavorites,
        isDisabled: isTalentActionDisabled,
        addToFavorites,
        overrideClientPreferredRate,
    } = useTalent();
    const [isClientPreferredRateOpen, setIsClientPreferredRateOpen] = useState(false)
    const [rate, setRate] = useState(talent.standardPlatformRate(false));

    const tooltipId = `${talent?.id}-browse`
    const favoriteId = "fav-btn-" + tooltipId;

    return (
        <div className="candidate-status-actions relative" id={`candidate-status-actions-${talent.id}`}>
            <div className="w-[35px] z-10 flex gap-4">
                {!bulkActions.length && props.onRemoveFromPool && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.stopPropagation();
                            if (isTalentActionDisabled) return;
                            props.onRemoveFromPool()
                        }}
                    >
                        <i className={"text-[#BFC5CB] text-2xl hover:text-primary fas fa-times"}/>
                    </button>
                )}
                {canRunBulkActions && onAddToBulkActions && (
                    <div onClick={e => e.stopPropagation()}>
                        <Checkbox
                            checked={isBulkActionsSelected}
                            onChange={async (e) => {
                                onAddToBulkActions()
                            }}
                            className="archive-checkbox ml-2"
                        />
                    </div>
                )}
            </div>

            <div
                className="self-start flex-grow flex flex-row h-8 absolute left-0 right-0 flex-nowrap justify-center gap-1.5 items-center mb-1">
                {talent.supplier_client?.id ? (
                    <React.Fragment>
                        <div className="whitespace-nowrap text-xxs self-center text-right">
                            Agency:
                        </div>
                        <Link target="_blank" to={`/agency-profiles/${talent.supplier_client.id}`}
                              onClick={e => e.stopPropagation()} className="flex gap-1 items-center justify-start">
                            <img src={talent.supplier_client.logo_path} className="h-8 img-fluid max-w-full"/>
                        </Link>
                    </React.Fragment>
                ) : (
                    <div className="whitespace-nowrap flex-1 text-xxs self-center text-center">
                        Freelancer
                    </div>
                )}
            </div>
            {!bulkActions?.length && canFavorite && (
                <React.Fragment>
                    <button
                        className="w-[35px] btn like-btn z-10"
                        style={styles.favoriteBtn}
                        onClick={(e) => {
                            e.stopPropagation();
                            onTalentAction(
                                talent.is_favorite ?
                                    removeFromFavorites :
                                    addToFavorites
                            )
                        }}
                        id={favoriteId}
                        disabled={isDisabled || isTalentActionDisabled}>
                        <i className={"text-danger fa-heart " + (talent.is_favorite ? "fas" : "far")}/>
                    </button>

                    <InfoTooltip key={favoriteId + 'tooltip'} type={"dark"} target={favoriteId}>
                        {talent.is_favorite ? "Remove from saved" : "Save as Favorite"}
                    </InfoTooltip>
                </React.Fragment>
            )}

            {!bulkActions?.length && (auth.isClientManager() || auth.isClientConsultant()) && (
                <Dropdown
                    onClick={(e) => e.stopPropagation()}
                    trigger={['click']}
                    placement="bottomRight"
                    className="cursor-pointer z-10"
                    getPopupContainer={() => document.getElementById(`candidate-status-actions-${talent.id}`)}
                    menu={{
                        items: [
                            {
                                key: 'email',
                                icon: <i className="fas fa-envelope"/>,
                                label: (
                                    <a href={`mailto:${talent.email}?subject=Message from Wripple Client Lead regarding ${talent.job_title}`}>
                                        {talent.email}
                                    </a>
                                ),
                            },
                            {
                                key: 'call',
                                icon: <i className="fas fa-phone"/>,
                                label: (
                                    <a href={`tel:${talent.phone}`}>
                                        {talent.phone}
                                    </a>
                                ),
                            },
                            {
                                key: 'set-rate',
                                icon: <i className="fas fa-dollar-sign"/>,
                                label: (
                                    <span className="whitespace-nowrap">Set Client Preferred Rate</span>
                                ),
                                onClick: (e) => {
                                    return setIsClientPreferredRateOpen(true)
                                },
                                disabled: isDisabled
                            },
                            ...(talent?.client_override_hourly_rate ? [
                                {
                                    key: 'delete-rate',
                                    icon: <i className="fas fa-eraser"/>,
                                    label: (
                                        <span className="whitespace-nowrap">Remove Client Preferred Rate</span>
                                    ),
                                    onClick: async (e) => {

                                        let confirm = await TB.alert("warning", "Remove Client Preferred Rate",
                                            "Are you sure you want to remove this talent's preferred rate for this client?",
                                        [
                                            {title: 'Yes, Continue'},
                                            {title: 'Cancel'}
                                        ])
                                        if (confirm) {
                                            return overrideClientPreferredRate(false)
                                        }
                                    },
                                    disabled: isDisabled
                                },
                            ] : [])
                        ],
                    }}
                >
                    <i className="fas fa-ellipsis-v ml-2"/>
                </Dropdown>
            )}
            {!bulkActions?.length && (auth.isClientManager() || auth.isClientConsultant()) && (
                <ClientPreferredRateModal
                    isDisabled={isDisabled}
                    onClose={setIsClientPreferredRateOpen}
                    isOpen={isClientPreferredRateOpen}/>
            )}
        </div>
    )
}

const TalentTile = ({children, ...props}) => {
    const {
        talent,
        matchingDisciplines: initialDisciplines,
        isDisabled: isTalentActionsDisabled,
        criteria
    } = useTalent();
    const location = useLocation();
    const [isReviewModalOpen, setIsReviewModalOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();

    const [matchingDisciplines, setMatchingDisciplines] = useState(initialDisciplines);
    // console.log('TalentTile', initialDisciplines)
    const detailLink = useMemo(() => {
        let params = new URLSearchParams(searchParams);

        return {
            pathname: `talent-profiles/${talent.id}`,
            search: params.size > 0 ? `?${params.toString()}` : undefined
        }
    }, [searchParams?.toString()])

    const showMatchDetails = () => {
        let matchCriteria = {};
        if (criteria) {
            matchCriteria = criteria;
        }

        return TB.event('Modal@show', {
            modalClass: 'match-details-modal',
            content: (
                <TalentMatchDetails
                    talent={talent}
                    isEngagementLead={false}
                    criteria={matchCriteria}
                />
            )
        })
    }

    const onTalentAction = async (request) => {
        let update;
        if (props.onRequest) {
            update = await props.onRequest(request);
        }else await request()
        console.log('onTalentAction', update)
        if (update && props.onUpdated) {
            await props.onUpdated(update);
        }
    }

    const imageSrc = useMemo(() => {
        if (talent?.image_path && String(talent?.image_path).includes('default-user')) {
            return null;
        }
        return talent?.image_path || TB.defaultUserProfileImagePath()
    }, [talent?.id])

    const metrics = useMemo(() => {
        if (!talent) return null;
        return talent.metricScores().map((metric, i) => {

            let tooltipId = `talent-${talent.id}-${i}`;

            let disabled = TB.isDefined(metric.overall?.max_count) && metric.overall.max_count === 0;

            return (
                <div key={tooltipId} className={
                    TB.mapClass("metric-match", {
                        disabled
                    }
                    )}>
                    <p>{metric.label}</p>
                    {metric.renderProgress()}

                </div>
            );
        })
    }, [talent?.id]);

    if (props.placeholder) {
        return <TalentTileSkeleton {...props}>{children}</TalentTileSkeleton>;
    }

    return (
        <SpinLoader isLoading={isTalentActionsDisabled}>
            <div className={TB.mapClass("candidate-panel", {
                selected: props.isBulkActionsSelected,
                'bulk-enabled': props.canRunBulkActions
            })} onClick={e => {
                console.log('e?.target?.tagName', e?.target?.tagName)
                if (e?.target?.tagName?.toLowerCase?.() !== 'a' && props.canRunBulkActions && props.onAddToBulkActions) {
                    props.onAddToBulkActions()
                }
            }}>

                {!props.canRunBulkActions && !props.hideAvailability && (
                    <div className="candidate-status-header shortlisted">
                        <p>
                            Available: {talent?.availableStartDate()}
                        </p>
                    </div>
                )}
                {props.header}


                <div className="pt-2">

                    <div className="px-4">
                        <TalentStatusActions
                            {...props}
                            isDisabled={props.isDisabled}
                            shortlistView={props.shortlistView}
                            onTalentAction={onTalentAction}
                            onRemoveFromPool={props.onRemoveFromPool}
                        />

                        <div className="candidate-top-contents">

                            <div className="content-row flex flex-col items-center">
                                <div className="candidate-card-image">
                                    <Img
                                        loader={<div className="loading-user-image"><LoadingSpinner size="lg"/></div>}
                                        src={imageSrc}
                                        unloader={<UserImageCircle user={talent} hideTooltip={true} size={105}
                                                                   className="img-fluid img-grayscale"/>}
                                        className="img-fluid img-grayscale"/>
                                </div>
                                <div className="candidate-name text-center mt-3">
                                    <h5 className="mb-0">
                                        {talent.firstNamePlusInitial()}
                                        {!props.shortlistView && talent.is_new &&
                                            <div className="new-indicator">NEW</div>}
                                        <div className="new-indicator">NEW</div>
                                    </h5>

                                    {talent.location && (
                                        <p className="mb-0">
                                            <i className="fa fa-map-marker-alt mr-1"/>
                                            {talent.location.city}{!!talent.location.state && `, ${talent.location.state}`}
                                        </p>
                                    )}
                                </div>
                                {props.includeRating && (
                                    <button type="button" onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        setIsReviewModalOpen(!isReviewModalOpen)
                                    }}
                                            className="mt-2 flex flex-col hover:underline items-center cursor-pointer rating-button">
                                        <Rate disabled allowHalf value={talent.avg_review_rating || 0}/>
                                        <p className="text-primary font-semibold text-lg">
                                            {TB.pluralize('Review', talent.reviews_count, true)}
                                        </p>
                                    </button>
                                )}

                            </div>

                            {talent?.hasMatching() && (
                                <div className="content-col">
                                    {/*<p>{matchingDisciplines?.length || '0'}/{criteria?.disciplines.length || '0'} Matched Roles</p>*/}
                                    <div className="matched-roles">
                                        {TB.map(TB.first(TB.chunk(matchingDisciplines, 2)), discipline => (
                                            <p className={TB.mapClass("role2", {
                                                'max-w-[234px]': matchingDisciplines?.length === 1,
                                                'max-w-[135px]': matchingDisciplines?.length === 2
                                            })} key={`r-${discipline.id}`}>
                                                {discipline.name}
                                            </p>
                                        ))}
                                        {matchingDisciplines?.length > 2 && (
                                            <>
                                                <p id={`matched-roles-${talent.id}`}
                                                   className="more-roles whitespace-nowrap">+ {TB.pluralize('role', matchingDisciplines?.length - 2, true)}</p>
                                                <InfoTooltip
                                                    type="dark"
                                                    placement="top"
                                                    target={`matched-roles-${talent.id}`}
                                                >
                                                    {matchingDisciplines.slice(2).map(discipline => {
                                                        return <p key={discipline.id}
                                                                  className="px-3 py-1 m-0 text-white text-left">{discipline.name}</p>
                                                    })}
                                                </InfoTooltip>
                                            </>
                                        )}
                                    </div>
                                </div>

                            )}

                            <div className="content-col text-center">
                                <p className="line-clamp candidate-bio">
                                    {talent.fullBio() || ''}
                                </p>
                                {/*                            <Link className="details-link"
                                  to={{
                                      ...location,
                                      pathname: `talent-profiles/${talent.id}`
                                  }} onClick={e => e.stopPropagation()}>
                                  to={detailLink}>
                                See Details
                            </Link>*/}
                                {!props.bulkActions?.length ? (
                                    <Link className="details-link"
                                          to={detailLink}>
                                        See Details
                                    </Link>
                                ) : (
                                    <div className="h-[18px] w-full"/>
                                )}
                            </div>
                        </div>

                        <hr className="m-0 mt-2"/>

                        {talent?.hasMatching() && (
                            <div className="talent-matching relative">
                                <div className="metric-container flex-col items-center self-center gap-3">

                                    <CircleProgress
                                        className="metric-circle w-28"
                                        progress={talent.overallMatchPercent()}
                                        strokeWidth={7}
                                    >
                                        <p>
                                            <span className="text-xl">{talent.overallMatchPercent(true)}%</span>
                                            <small className="text-xs">Match</small>
                                        </p>
                                    </CircleProgress>

                                    <button type="button" className="details-link" onClick={e => {
                                        e.stopPropagation();
                                        showMatchDetails();
                                    }}>More...
                                    </button>

                                </div>
                                <TalentTileBadges/>
                            </div>
                        )}
                    </div>


                    <TalentTileRate talent={talent} onUpdate={props.onUpdated}/>

                    {children}


                </div>

                {isReviewModalOpen && (
                    <TalentReviewModal
                        isOpen={isReviewModalOpen}
                        onClose={() => setIsReviewModalOpen(false)}
                        imageSrc={imageSrc}
                        talent={talent}
                        onUpdateTalent={props.onUpdated}
                    />
                )}
            </div>
        </SpinLoader>
    )
}

const TalentTileSkeleton = ({children, ...props}) => {
    return (
        <div className="talent-panel candidate-panel potential-talent-panel relative">

            {props.hideAvailability && <div className="candidate-status-header shortlisted"/>}

            <div className="flex flex-col items-center gap-2 p-4">
                <div className="skeleton h-3 w-1/5"/>

                <div className="skeleton rounded-full h-[105px] w-[105px]"/>

                <div className="skeleton h-5 w-1/2"/>
                <div className="skeleton h-3 w-1/5 mt-1"/>
                <div className="skeleton h-3 w-1/6"/>

                <div className="flex gap-3 w-5/6">
                    <div className="skeleton h-6 w-full"/>
                    <div className="skeleton h-6 w-full"/>
                </div>

                <div className="skeleton h-10 w-11/12 mt-2"/>

                <div className="skeleton h-3 w-1/5"/>

                {/* <div className="h-px bg-black w-full" /> */}
                <hr className="border-wrippleLightGrayBg w-full"/>

                <div className="skeleton rounded-full h-28 w-28 flex items-center justify-center">
                    <div className="bg-white rounded-full h-24 w-24" />
                </div>

                <div className="skeleton h-3 w-1/5" />

            </div>

            <div className="candidate-rate-section items-center">
                <div className="skeleton h-5 mb-2 w-1/3" />
                <div className="skeleton h-4 w-1/5" />
            </div>

            {/* <div className="h-[120px] flex items-center justify-center px-5">
                <div className="skeleton-purple h-9 w-full"></div>
            </div> */}
            {children}
        </div>
    )
}

const TalentTileSkeleton2 = ({ children, ...props }) => {

    const metricLabels = [
        (<span key="skill-plh">
            <span>Skill</span>
            <span>Expertise</span>
        </span>),
        (<span key="exp-plh">
            <span>Years</span>
            <span>Experience</span>
        </span>),
        (<span key="industry-plh">
            <span>Industry</span>
            <span>Experience</span>
        </span>),
    ];

    return (
        <div className="talent-panel candidate-panel potential-talent-panel relative">

            {!props.canRunBulkActions && (
                <div className="candidate-status-header">
                    <p>
                        Available: TBD
                    </p>

                </div>
            )}

            <div className="card pt-2 border-none flex-1">
                <div className="team-info-panel talent-info-panel">

                    <div className="top-info">

                        <div className="talent-info-placeholder" style={{
                            width: 100,
                            height: 22,
                            alignSelf: 'center',
                        }} />

                        <div className="team-info-container">
                            <span className="truncating-text team-info-text">
                                <div className="talent-info-placeholder" style={{
                                    width: 90,
                                    height: 10,
                                }} />
                            </span>

                            <span className="truncating-text team-info-text">
                                <div className="talent-info-placeholder" style={{
                                    width: 90,
                                    height: 10,
                                }} />
                            </span>
                        </div>


                        <div className="talent-info-placeholder" style={{
                            height: 14,
                            width: 300,
                            alignSelf: 'center',
                        }} />
                        <div className="talent-info-placeholder" style={{
                            height: 14,
                            width: 80,
                            alignSelf: 'center',
                        }} />
                        <p className="details-link" style={{
                            color: '#334D6E',
                            opacity: 0.6,
                        }}>

                        </p>
                    </div>

                    <div className="talent-matching">

                        <div className="overall-matching px-4">
                            <p className="overall-progress">
                                <span style={{ opacity: 0.6 }}></span>
                            </p>
                            <ProgressBar
                                filledBackground={TB.config('color.orange')}
                                height={20}
                                percent={0}
                                unfilledBackground={TB.config('color.gray')} />
                        </div>


                        <div className="metric-container">

                            {metricLabels.map((label, i) => (
                                <div key={`talent-${i}`} className="metric-match disabled">
                                    <p>{label}</p>
                                    <CircleProgress
                                        className="metric-circle"
                                        progress={0}
                                        strokeWidth={7}>
                                        <p>
                                            <span>
                                                <span></span>
                                                <small></small>
                                            </span>
                                        </p>
                                    </CircleProgress>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="candidate-rate-section">
                        <h4>Standard Rate</h4>
                        <h2 className="currency-sup">
                            -
                            <span>/hr</span>
                        </h2>
                    </div>

                    {children}

                </div>
            </div>
        </div>
    )
}

export default TalentTile;


const styles = {
    favoriteBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-end'
    },

    archiveBtn: {
        display: 'flex',
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'flex-start'
    },
    inviteLockIcon: {
        position: 'absolute',
        fontSize: 13,
        right: -18,
    }
};

const InvitedTalentTile = ({ invited, onRemoveFromPool }) => {
    const { talent, isDisabled } = useTalent();

    return (
        <div className="candidate-panel">


            <div className="candidate-status-header shortlisted bg-wrippleLightDarkest">
                <p>Invited: {TB.moment(talent.invited_at).isValid() ? TB.moment(talent.invited_at).format('ll') : 'N/A'}</p>
            </div>

            <div className="pt-2">
                <div className="px-4">
                    <div className="candidate-top-contents gap-8 py-4">

                        <div className="content-row flex flex-col items-center">
                            <div className="candidate-card-image">
                                <UserImageCircle user={talent} hideTooltip={true} size={105} className="img-fluid img-grayscale" />
                            </div>
                            <div className="candidate-name text-center mt-3">
                                <h5 className="mb-0">{talent.firstNamePlusInitial()}</h5>
                                <p className="pt-2 text-sm text-wrippleLightDarkest">{talent.email}</p>
                            </div>
                        </div>

                        <p className="text-xl text-center text-wrippleLightDarkest">Not Yet Rated</p>

                        <div className="content-col text-center">
                            <p className="line-clamp candidate-bio">
                                This person has not yet added a Profile. They can add one once accepting your invitation.
                            </p>
                        </div>
                    </div>

                    <hr className="m-0 mt-2" />
                </div>

                <div className="candidate-rate-section bg-[#EEE]">
                    <h4 className="text-wrippleLightDarkest">Current rate unavailable</h4>
                    <h2 className="currency-sup text-wrippleLightDarkest">
                        {talent.rangedPlatformRates()}
                        <span>/hr</span>
                    </h2>
                </div>
                <div className="bg-white flex flex-col items-stretch px-4 py-6 justify-center">
                    <Button disabled={isDisabled} type="primary"  onClick={() => onRemoveFromPool()}>
                        Remove from Pool
                    </Button>
                </div>

            </div>
        </div>

    )
}

export {
    TalentTileSkeleton, InvitedTalentTile
}